// One rep's day (WEB_PLAN.md §5.4 "Rep detail"), the pure half of the port of RepDetailView
// (GroundTeam/Views/Team/RepDetailView.swift) and its store helpers (Team+Store.swift: dayLog(for:on:),
// fieldActivities(for:on:), location(of:)): which day is shown, that day's completed activities, the GPS trail, the
// numbered visit pins and the in-field time. No React here, so it is unit-tested in node.

import type { ActivityPayload, DayLogPayload, RecordPayload, UserPayload, VisitResultPayload } from '@/contracts/models';
import { isSameDay, startOfDay, addDays } from '@/domain/calendar';
import { effectiveMs } from '@/domain/derived';
import { isValidCoordinate, type Coordinate } from '@/domain/geo';
import { lookup, ms, sameId, trimAll } from '@/domain/util';

// MARK: - The day

/** `YYYY-MM-DD` of the local calendar day (the `?day=` query value). */
export function dayParam(day: Date): string {
  const y = String(day.getFullYear()).padStart(4, '0');
  const m = String(day.getMonth() + 1).padStart(2, '0');
  const d = String(day.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * The day a `?day=` value names, as local midnight. Missing, malformed or future values give today: the picker never
 * goes past today (`DatePicker(in: ...Date.now)`).
 */
export function parseDayParam(value: string | null | undefined, now: Date = new Date()): Date {
  const today = startOfDay(now);
  const match = value ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(value) : null;
  if (!match) return today;
  const [y, m, d] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const day = new Date(y, m - 1, d);
  // Rejects 2026-02-31 and friends (Date would roll them over into the next month).
  if (day.getFullYear() !== y || day.getMonth() !== m - 1 || day.getDate() !== d) return today;
  return day.getTime() > today.getTime() ? today : day;
}

/** The day `delta` days away, or null when that is in the future (`shift(_:)`). */
export function shiftDay(day: Date, delta: number, now: Date = new Date()): Date | null {
  const next = addDays(startOfDay(day), delta);
  return next.getTime() > startOfDay(now).getTime() ? null : next;
}

// MARK: - Activities

/** `fieldActivities(for:on:)`: the rep's completed activities whose effective date falls on `day`, oldest first. */
export function dayActivities(activities: readonly ActivityPayload[], userId: string, day: Date): ActivityPayload[] {
  return activities
    .filter((a) => a.status === 'completed' && sameId(a.userId, userId) && isSameDay(new Date(effectiveMs(a)), day))
    .sort((a, b) => effectiveMs(a) - effectiveMs(b));
}

/** `location(of:)`: where a visit was logged from, else the store's pin; nothing for invalid or (0,0) points. */
export function visitLocation(
  activity: Pick<ActivityPayload, 'location' | 'recordId'>,
  recordOf: (id: string | undefined) => Pick<RecordPayload, 'coordinate'> | undefined,
): Coordinate | undefined {
  if (isValidCoordinate(activity.location)) return activity.location;
  const c = recordOf(activity.recordId)?.coordinate;
  return isValidCoordinate(c) ? c : undefined;
}

/** iOS PinMarker default when a visit has no (known) result. */
export const VISIT_PIN_DEFAULT_COLOR = '#F96A3C';

export interface VisitPin {
  /** The activity id. */
  id: string;
  recordId?: string;
  coordinate: Coordinate;
  color: string;
  /** "1", "2", … in the order of the day (unlocated visits keep their number, as on iOS). */
  label: string;
}

/** Numbered visit pins colored by the visit result, for visits that have a location. */
export function visitPins(
  visits: readonly ActivityPayload[],
  recordOf: (id: string | undefined) => Pick<RecordPayload, 'coordinate'> | undefined,
  resultsById: ReadonlyMap<string, Pick<VisitResultPayload, 'colorHex'>>,
): VisitPin[] {
  const pins: VisitPin[] = [];
  visits.forEach((visit, index) => {
    const coordinate = visitLocation(visit, recordOf);
    if (!coordinate) return;
    pins.push({
      id: visit.id,
      ...(visit.recordId ? { recordId: visit.recordId } : {}),
      coordinate,
      color: lookup(resultsById, visit.resultId)?.colorHex ?? VISIT_PIN_DEFAULT_COLOR,
      label: String(index + 1),
    });
  });
  return pins;
}

// MARK: - Trail

/** The day's breadcrumbs that are real positions (`trail`). */
export function trailCoordinates(log: Pick<DayLogPayload, 'points'> | null | undefined): Coordinate[] {
  return (log?.points ?? []).flatMap((p) =>
    isValidCoordinate(p.coordinate) ? [{ latitude: p.coordinate.latitude, longitude: p.coordinate.longitude }] : [],
  );
}

export interface TrailFeatureCollection {
  type: 'FeatureCollection';
  features: { type: 'Feature'; properties: Record<string, never>; geometry: { type: 'LineString'; coordinates: [number, number][] } }[];
}

/** GeoJSON for the trail line (empty below two points: one point is drawn as the start marker only). */
export function trailGeoJson(coords: readonly Coordinate[]): TrailFeatureCollection {
  if (coords.length < 2) return { type: 'FeatureCollection', features: [] };
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: {},
        geometry: { type: 'LineString', coordinates: coords.map((c) => [c.longitude, c.latitude]) },
      },
    ],
  };
}

/**
 * Content keys for the map's inputs. The lists are rebuilt whenever any activity, store or day log in the company
 * syncs (every poll that brings something); the keys change only when what the map draws does, so the map keeps its
 * sources and camera instead of re-uploading and re-fitting on unrelated syncs.
 */
export function coordinatesKey(coords: readonly Coordinate[]): string {
  return coords.map((c) => `${c.latitude},${c.longitude}`).join(';');
}

export function visitPinsKey(pins: readonly VisitPin[]): string {
  return pins
    .map((p) => `${p.id}|${p.label}|${p.color}|${p.recordId ?? ''}|${p.coordinate.latitude},${p.coordinate.longitude}`)
    .join(';');
}

/** The trail is still growing: today, and the rep has not ended the day ("Now" instead of "End"). */
export function isLiveTrail(log: Pick<DayLogPayload, 'endedAt'> | null | undefined, isToday: boolean): boolean {
  return isToday && !log?.endedAt;
}

// MARK: - Time in the field

/**
 * `fieldTimeText`: from `startedAt ?? firstActivityAt` to `endedAt ?? lastActivityAt ?? now` (now only today, and
 * only when the rep started the day). Null when there is no log, no start, no end, or the end is not after the start.
 */
export function fieldMinutes(
  log: Pick<DayLogPayload, 'startedAt' | 'firstActivityAt' | 'endedAt' | 'lastActivityAt'> | null | undefined,
  isToday: boolean,
  now: Date = new Date(),
): number | null {
  if (!log) return null;
  const start = ms(log.startedAt) ?? ms(log.firstActivityAt);
  const end = ms(log.endedAt) ?? ms(log.lastActivityAt) ?? (isToday && log.startedAt !== undefined ? now.getTime() : undefined);
  if (start === undefined || end === undefined || end <= start) return null;
  return Math.floor((end - start) / 60_000);
}

/** As iOS prints it: "2h 5m", "45m", or "—" when unknown. */
export function formatFieldTime(minutes: number | null): string {
  if (minutes === null) return '—';
  return minutes >= 60 ? `${Math.floor(minutes / 60)}h ${minutes % 60}m` : `${minutes}m`;
}

export interface Milestone {
  title: string;
  time: Date | null;
  /** First or last line of the timeline. */
  edge: 'start' | 'end';
  /** Recorded (sunrise / sunset), still out in the field, or nothing recorded (gray). */
  tone: 'recorded' | 'live' | 'none';
}

/** The timeline's first and last lines (`timelineSection`). `end` is null when there is nothing to say. */
export function dayMilestones(
  log: Pick<DayLogPayload, 'startedAt' | 'firstActivityAt' | 'endedAt' | 'lastActivityAt'> | null | undefined,
  isToday: boolean,
): { start: Milestone; end: Milestone | null } {
  const startMs = ms(log?.startedAt) ?? ms(log?.firstActivityAt);
  const start: Milestone =
    startMs !== undefined
      ? { title: 'Start of day', time: new Date(startMs), edge: 'start', tone: 'recorded' }
      : { title: isToday ? 'Day not started' : 'No start recorded', time: null, edge: 'start', tone: 'none' };
  const endMs = ms(log?.endedAt);
  let end: Milestone | null = null;
  if (endMs !== undefined) {
    end = { title: 'End of day', time: new Date(endMs), edge: 'end', tone: 'recorded' };
  } else if (log?.startedAt !== undefined) {
    const last = ms(log.lastActivityAt);
    end = {
      title: isToday ? 'Still in the field' : 'Day never ended',
      time: last !== undefined ? new Date(last) : null,
      edge: 'end',
      tone: isToday ? 'live' : 'none',
    };
  }
  return { start, end };
}

// MARK: - Contact links

export interface ContactLinks {
  sms?: string;
  tel?: string;
  mail?: string;
}

/** `User.telURL` / `smsURL` / `mailURL`: digits (and +) only for the phone; an email needs an "@". */
export function contactLinks(user: Pick<UserPayload, 'phone' | 'email'>): ContactLinks {
  const dialable = Array.from(user.phone).filter((c) => '+0123456789'.includes(c)).join('');
  const email = trimAll(user.email);
  const links: ContactLinks = {};
  if (dialable !== '') {
    links.sms = `sms:${dialable}`;
    links.tel = `tel:${dialable}`;
  }
  if (email.includes('@')) links.mail = `mailto:${encodeURI(email)}`;
  return links;
}

/** "3 activities" / "1 activity". */
export function activityCountText(n: number): string {
  return `${n} activit${n === 1 ? 'y' : 'ies'}`;
}
